import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { EMPTY, merge, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { Logger, TrackService } from '@aiwyn/shared';
import {
  HydrateCxpDeliverableDetailFromTasks,
  LoadCxpDeliverableDetail,
} from '../state/cxp-deliverable-detail.actions';
import {
  CxpDeliverableDetailSelectors,
  CxpDeliverableDetailViewModel,
} from '../state/cxp-deliverable-detail-state.selectors';
import { CxpTasksSelectors } from '../state/cxp-tasks-state.selectors';
import { CxpRouteParamsService } from './cxp-route-params.service';

const DEFAULT_VIEW_MODEL: CxpDeliverableDetailViewModel = {
  detail: null,
  isLoading: false,
  isLoaded: false,
  hasError: false,
};

/**
 * Facade for the deliverable-detail page. Mirrors `CxpTasksDataStateService`:
 * exposes signals for the container and a `backgroundEffects$()` observable
 * the container subscribes to in its constructor.
 *
 * Direct URL loads work because:
 *   1. Injecting this service injects `CxpRouteParamsService`, whose
 *      constructor reads the router snapshot and dispatches the initial
 *      `SetCxpActiveRouteParams` synchronously.
 *   2. The hydrate + load effects select on `selectedDeliverableId` via the
 *      store — when the container subscribes, the current value is emitted
 *      immediately and both fire.
 *
 * When navigating from the tasks list, the hydrate effect populates the
 * header (name, description, service line, client) from data already in
 * `CxpTasksState`, so the page renders instantly. The load effect runs in
 * parallel and replaces the slice with the full backend payload (files,
 * contacts) when it completes.
 */
@Injectable({ providedIn: 'root' })
export class CxpDeliverableDetailDataStateService {
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);
  private readonly trackService = inject(TrackService);
  // Injected to bootstrap route param tracking — its constructor sets up the
  // NavigationEnd subscription and dispatches the initial snapshot value.
  private readonly _routeParams = inject(CxpRouteParamsService);

  readonly viewModel = toSignal(this.store.select(CxpDeliverableDetailSelectors.getViewModel), {
    initialValue: DEFAULT_VIEW_MODEL,
  });

  /**
   * Combine observables that react to NGXS state changes and dispatch actions
   * as side effects. The caller is responsible for subscribing and managing
   * the subscription lifetime (e.g. takeUntilDestroyed).
   */
  backgroundEffects$(): Observable<unknown> {
    return merge(
      this.hydrateFromTasksOnSelected$().pipe(catchError(() => EMPTY)),
      this.loadDetailOnSelected$().pipe(catchError(() => EMPTY)),
    );
  }

  private hydrateFromTasksOnSelected$(): Observable<unknown> {
    return this.store.select(CxpDeliverableDetailSelectors.getSelectedDeliverableId).pipe(
      distinctUntilChanged(),
      filter((id): id is number => id !== null),
      tap((deliverableId: number) => {
        const partial = this.store.selectSnapshot(
          CxpTasksSelectors.getDeliverableHydrationFromTasks(deliverableId),
        );
        if (partial) {
          this.store.dispatch(new HydrateCxpDeliverableDetailFromTasks(partial));
        }
      }),
      catchError((error) => {
        this.logger.error('[CxpDeliverableDetailDataStateService] hydrateFromTasksOnSelected$ failed:', error);
        return EMPTY;
      }),
    );
  }

  private loadDetailOnSelected$(): Observable<unknown> {
    return this.store.select(CxpDeliverableDetailSelectors.getSelectedDeliverableId).pipe(
      distinctUntilChanged(),
      filter((id): id is number => id !== null),
      switchMap((deliverableId) => {
        this.trackService.track('CXP Deliverable Detail Viewed', { deliverableId });
        return this.store.dispatch(new LoadCxpDeliverableDetail(deliverableId));
      }),
      catchError((error) => {
        this.logger.error('[CxpDeliverableDetailDataStateService] loadDetailOnSelected$ failed:', error);
        return EMPTY;
      }),
    );
  }
}
